import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface ScrollingAircraftProps {
  className?: string;
}

const MOBILE_QUERY = '(max-width: 767px)';

const ScrollingAircraft: React.FC<ScrollingAircraftProps> = ({ className = '' }) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false
  );
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const aircraftRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Check if user has started scrolling
      if (currentScrollY > 0 && !hasScrolled) {
        setHasScrolled(true);
      }
      
      // Determine scroll direction
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up');
      }
      
      lastScrollY.current = currentScrollY;
      setScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [hasScrolled, isMobile]);

  // Calculate rotation based on scroll state
  const getRotation = () => {
    let rotation = 0;
    
    if (!hasScrolled) {
      // Initial state: 90 degrees clockwise
      rotation = 90;
    } else if (scrollDirection === 'down') {
      // Rotate 180 degrees clockwise when scrolling down
      rotation = 180;
    } else if (scrollDirection === 'up') {
      // Rotate 360 degrees clockwise when scrolling up
      rotation = 360;
    } else {
      // Keep at 0 degrees when idle after scrolling
      rotation = 0;
    }
    
    return rotation;
  };

  // Calculate size based on scroll state
  const getSize = () => {
    if (!hasScrolled) {
      // Initial state: small size
      return 30;
    }
    
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const maxScroll = documentHeight - windowHeight;
    const scrollProgress = scrollY / maxScroll;
    
    // Small at top (0-20% of scroll) or bottom (80-100% of scroll)
    // Large in middle (20-80% of scroll)
    if (scrollProgress <= 0.2 || scrollProgress >= 0.8) {
      return 30; // Small at top and bottom
    } else {
      return 60; // Large in middle
    }
  };

  // Calculate vertical position based on scroll state
  const getVerticalPosition = () => {
    if (!hasScrolled) {
      // Initial state: fixed position at top
      return 20; // Fixed at 20% from top
    }
    
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const maxScroll = documentHeight - windowHeight;
    
    // Move aircraft down with scroll, but keep it within viewport bounds
    const basePosition = 15; // Start at 15% from top
    const scrollProgress = Math.min(scrollY / maxScroll, 1);
    const maxMovement = 70; // Max 70% movement
    
    return basePosition + (scrollProgress * maxMovement);
  };

  if (isMobile) {
    return null;
  }

  return (
    <motion.div
      ref={aircraftRef}
      className={`fixed right-2 md:right-8 z-30 pointer-events-none ${className}`}
      style={{
        top: `${getVerticalPosition()}%`,
      }}
      initial={{ opacity: 0, x: 100 }}
      animate={{ 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.8, ease: 'easeOut' }
      }}
    >
      {/* Aircraft SVG */}
      <motion.div 
        className="relative"
        animate={{
          rotate: getRotation(),
          scale: getSize() / 60, // Scale relative to base size of 60
        }}
        transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }}
      >
        <img
          src="/frontend-assets/aircraft.svg"
          alt="Aircraft"
          width="60"
          height="60"
          className="drop-shadow-lg filter"
          style={{
            filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
          }}
        />
        
        {/* Optional: Add a subtle glow effect */}
        <div 
          className="absolute inset-0 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, #2895d5 0%, transparent 70%)',
            filter: 'blur(8px)',
            transform: 'scale(1.2)',
          }}
        />
      </motion.div>
      
    </motion.div>
  );
};

export default ScrollingAircraft;
